import { ChangeEvent, FocusEvent, useCallback, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { useTranslate } from '@growtherapy/i18n';
import { InputControl, TextInput } from '@growtherapy/sprout-ui';

import { validateVisitorName } from './useVisitorName';

export type VisitorNameInputProps = {
  autoFocus?: boolean;
  className?: string;
  disabled?: boolean;
  isRequired: boolean;
  label?: string;
  onEnter?: () => void;
  setVisitorName: (visitorName: string, validationResult: true | string) => void;
  visitorName: string;
};

export function VisitorNameInput({
  autoFocus,
  className,
  disabled,
  isRequired,
  label,
  onEnter,
  setVisitorName,
  visitorName,
}: VisitorNameInputProps) {
  const t = useTranslate();
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState<string>();

  const handleOnChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const validationResult = validateVisitorName(t, e.target.value, isRequired);
      // Only show validation error if the field has been touched, to prevent
      // over-aggressive error feedback while the user is filling in an initial
      // value.
      if (touched) {
        setError(validationResult !== true ? validationResult : undefined);
      }
      setVisitorName(e.target.value, validationResult);
    },
    [t, isRequired, touched, setVisitorName],
  );

  const handleOnBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      setTouched(true);
      const validationResult = validateVisitorName(t, e.target.value, isRequired);
      setError(validationResult !== true ? validationResult : undefined);
    },
    [t, isRequired],
  );

  // workaround; let enter keypress trigger input validation
  const handleOnEnter = useCallback(() => {
    setTouched(true);
    const validationResult = validateVisitorName(t, visitorName, isRequired);
    setError(validationResult !== true ? validationResult : undefined);
    onEnter?.();
  }, [t, visitorName, isRequired, onEnter]);

  const nameLabel = label ?? t('telehealth-core:introRoom.whatNameWouldYouLikeToGoBy');
  const optionalLabel = isRequired ? '' : ` ${t('telehealth-core:core.optionalParenthesis')}`;

  return (
    <InputControl
      className={twMerge('mb-0', className)}
      disabled={disabled}
      label={`${nameLabel}${optionalLabel}`}
      required={isRequired}
      error={error}
    >
      <TextInput
        // Generally this prop is only set when this field is the first or only
        // focusable field on the page and the form is the primary purpose of
        // that page. In this case, autofocusing the field is sensible because
        // it's where the user's attention is drawn anyway.
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={autoFocus}
        disabled={disabled}
        value={visitorName}
        onChange={handleOnChange}
        onBlur={handleOnBlur}
        onEnter={handleOnEnter}
        mask='exclude'
        placeholder={t('telehealth-core:formCore.chosenName')}
        name='visitor-name'
        autoComplete='given-name'
      />
    </InputControl>
  );
}
